<template>
  <div id="annotate-canvas" ref="annotateCanvas" @drop="drop" @dragover="dragOver" tabindex="0" v-on:keydown="handleKeyDown">
    <async-block id="async-block" :loading="this.$store.state.item.attributes.status != 'completed'" v-show="!uploading">
      <div id="controllers">
        <div id="zoom">
          <div id="zoom-out"><img :src="require(`images/annotate/tools-zoom-zoom-out.svg`)" @click="zoomOut"/></div>
          <span>{{this.scalePct}}%</span>
          <div id="zoom-in"><img :src="require(`images/annotate/tools-zoom-zoom-in.svg`)"  @click="zoomIn"/></div>
        </div>
        <div id="history">
          <div id="undo" title="Undo (Ctrl + Z or Cmd + Z)">
            <img :src="undoImage" @click="undoChange"/>
          </div>
          <div id="redo" title="Redo (Ctrl + Y or Cmd + Y)">
            <img :src="redoImage"  @click="redoChange"/>
          </div>
        </div>
      </div>
      <div :style="{visibility: cropping ? 'visible' : 'hidden'}" id="crop-confirmation" ref="cropConfirmation">
        <a class="btn btn-primary" @click="confirmCrop">Apply</a>
        <a class="btn btn-danger" @click="cancelCrop">Cancel</a>
      </div>
      <div id="image" class="image" ref="image">
        <img :src="imageUrl" @load="onImageLoad()" ref="imageElement" crossorigin="anonymous" v-show="false">
      </div>
    </async-block>
    <img id="spinner" :src="require('images/cloud-loader.gif')" v-show="uploading">
  </div>
</template>

<script>
  import '@/src/konva/lineTransformer';
  import '@/src/konva/circledNumber';
  import Konva from 'konva';
  import _ from "lodash";
  import EventBus from '../event_bus';
  import { mapState } from 'vuex';
  import { linear } from 'everpolate';
  import {
    getDrawDirection,
    getLineAngles
   } from '@/src/annotateHelper';

  Konva.Stage.prototype.getScaledPointerPosition = function() {
    const pointerPosition = this.getPointerPosition();
    // Use base layer as base for dimentioning, both base layer and draw layer are the same size
    const layer = _.find(this.getLayers(), (layer) => layer.attrs.id == 'baseLayer');
    const x = (pointerPosition.x - layer.x()) / layer.scaleX();
    const y = (pointerPosition.y - layer.y()) / layer.scaleY();
    return {x: x, y: y};
  }

  export default {
    props: {
      selectedColor: {
        type: String
      },
      selectedTool: {
        type: String
      },
      selectedThickness: {
        type: Number
      },
      selectedFontSize: {
        type: Number
      },
      outlineText: {
        type: Boolean
      },
      dirty: {
        type: Boolean
      },
      uploading: {
        type: Boolean
      }
    },
    data: () => {
      return {
        routes: require("routes.js.erb"),
        width: 0,
        height: 0,
        stage: null,
        layer: null,
        baseLayer: null,
        cropping: false,
        scrollLayer: null,
        scrollBarX: null,
        scrollBarY: null,
        scrollBarPadding: 5,
        currentShape: null,
        currentTransformation: null,
        overShape: false,
        drawing: false,
        baseImage: null,
        changes: { redo: [], undo: [], userUndone: false },
        scalePct: 100,
        scaleFactor: 1,
        // these images are used to keep references for image nodes, node.toJSON() does not keep the image in it and we need to inject it later when
        // recreating the layer when user undoes/redoes copies/pastes
        images: {},
        textEditing: false,
        outlineWidths: { 20: 6, 40: 7, 60: 11},
        typingAfterArrow: false,
        numberToolIncrement: 1,
      }
    },
    activated: function(){
      this.resetCanvas();
      // if user came back to async annotations and image has already been loaded
      // onImageLoad won't initialize with the new image since it was not active, need to do that here.
      if(!this.uploading){
        this.initialize(this.$refs.imageElement);
      }
    },
    mounted: function() {
      const vm = this;
      EventBus.$on('annotate:upload', vm.upload);
      EventBus.$on('annotate:close', vm.resetCanvas);
      EventBus.$on('annotate:undo', vm.undoChange);
      EventBus.$on('annotate:redo', vm.redoChange);
      EventBus.$on('annotate:delete', vm.deleteCurrentShape);

      document.addEventListener("cut", vm.cut);
      document.addEventListener("copy", vm.copy);
      document.addEventListener("paste", vm.paste);
    },
    methods: {
      onImageLoad() {
        // Only initialize when in annotate route,
        // updating image source directly in upload method via store
        // causes this component to trigger this handler when its not active.

        if(this.$route.name == 'annotate') {
          this.initialize(this.$refs.imageElement);
          this.$forceUpdate();
          this.$emit('update:uploading', false);
          this.$emit('update:dirty', false);
        }
      },
      initialize(img) {
        const vm = this;
        const maxHeight = vm.$refs.annotateCanvas.clientHeight;
        const maxWidth = vm.$refs.annotateCanvas.clientWidth;

        // put image and annotations on different layers for easier cleanup.
        vm.layer = new Konva.Layer({ id: 'drawLayer' });
        vm.baseLayer = new Konva.Layer({ id: 'baseLayer' });
        vm.scrollLayer = new Konva.Layer({ id: 'scrollLayer' });

        vm.stage = new Konva.Stage({
          container: 'image',
          width: maxWidth,
          height: maxHeight,
        });

        vm.scrollBarY = new Konva.Rect({
          width: 10,
          height: 100,
          fill: 'grey',
          opacity: 0.8,
          x: vm.stage.width() - vm.scrollBarPadding - 10,
          y: vm.scrollBarPadding,
          draggable: true,
          visible: false,
          dragBoundFunc: function (pos) {
            pos.x = vm.stage.width() - vm.scrollBarPadding - 10;
            pos.y = Math.max(
              Math.min(pos.y, vm.stage.height() - this.height() - vm.scrollBarPadding),
              vm.scrollBarPadding
            );
            return pos;
          },
        });

        vm.scrollBarX = new Konva.Rect({
          width: 100,
          height: 10,
          fill: 'grey',
          opacity: 0.8,
          x: vm.scrollBarPadding,
          y: vm.stage.height() - vm.scrollBarPadding - 10,
          draggable: true,
          visible: false,
          dragBoundFunc: function (pos) {
            pos.x = Math.max(
              Math.min(pos.x, vm.stage.width() - this.width() - vm.scrollBarPadding),
              vm.scrollBarPadding
            );
            pos.y = vm.stage.height() - vm.scrollBarPadding - 10;

            return pos;
          },
        });

        vm.scrollLayer.add(vm.scrollBarX).add(vm.scrollBarY).draw();

        vm.scrollBarY.on('mousedown', (e) => e.cancelBubble = true);
        vm.scrollBarX.on('mousedown', (e) => e.cancelBubble = true);

        vm.scrollBarY.on('dragmove', () => vm.scroll('y'));
        vm.scrollBarX.on('dragmove', () => vm.scroll('x'));

        vm.setBaseImageAndScale(img);

        // this order is important as it dictates zindex of elements.
        vm.stage.add(vm.baseLayer);
        vm.stage.add(vm.layer);
        vm.stage.add(vm.scrollLayer);

        // clicking anywhere in the image stops ongoing transformation
        vm.stage.on('mousedown', vm.stopTransformation);
        vm.stage.on('mousedown', vm.initShape);
        vm.stage.on('mousemove', vm.updateShape);
        vm.stage.on('mouseup', vm.finishShape);
        vm.stage.on('wheel', vm.scaleOrScroll);
        vm.saveStageState();
      },
      adjustStageBoundaries: function(){
        const vm = this;
        // if nothing is drawn pass the image, the image itself would have the lowest and highest points.
        let minX = vm.baseImage.attrs.x;
        let minY = vm.baseImage.attrs.y;
        let maxX = minX + vm.baseImage.width();
        let maxY = minY + vm.baseImage.height();

        // Go through each of the shapes to find if user draw outside of the
        vm.layer.children.forEach((shape, index) => {
          const type = shape.constructor.name;
          // we don't care about transformer nodes
          if(type == "Transformer") { return; }
          // exclude any empty text nodes from calc;
          if(type == "Text" && !shape.attrs.hasText) { return; }

          let strokeWidth = shape.attrs.strokeWidth || 0;
          let x, y, x1, y1;
          switch(type){
            // LineTransformer handles arrows, highlight and line (not free drawing line)
            case 'LineTransformer':
              // Need to get the line from the group
              const realShape = shape.getNode();
              [x, y, x1, y1] = realShape.points();
              // fix strokewidth as we need to get it from the shape in the group, not the group like in line 236
              strokeWidth = (realShape.attrs.strokeWidth || 0) / 2;
              break;
            case 'Line':
              [x, y, x1, y1] = shape.points();
              if(shape.attrs.drawing){
                // drawings have many points, first and last and not necesarily at the edges, go through all points and get the edges.
                // TODO: This could be done while drawing? Maybe impacts UX as it may slow down the actual drawing?
                shape.attrs.points.forEach((_, i, a) => {
                  let pointX, pointY;
                  if(i % 2 == 0) {
                    [pointX, pointY] = a.slice(i, i+2);
                    if(pointX < x) { x = pointX } else { x1 = pointX };
                    if(pointY < y) { y = pointY } else { y1 = pointY };
                  }
                });
              }
              strokeWidth /= 2;
              break;
            case 'Rect':
            case 'Image':
            case  'CircledNumber':
              x = shape.attrs.x;
              y = shape.attrs.y;
              // width and height come negative if drawn from right to left so it's good just to add
              x1 = x + shape.attrs.width;
              y1 = y + shape.attrs.height;
              strokeWidth /= 2;
              break;

            case 'Ellipse':
              x = shape.attrs.x - shape.attrs.radiusX;
              y = shape.attrs.y - shape.attrs.radiusY;
              x1 = shape.attrs.x + shape.attrs.radiusX;
              y1 = shape.attrs.y + shape.attrs.radiusY;
              break;

            case 'Text':
              x = shape.attrs.x;
              y = shape.attrs.y;
              x1 = x + shape.attrs.realWidth;
              y1 = y + shape.attrs.realHeight;
              break;

          }

          // Konva returns dimensions without considering strokeWidth, need to add this to calculations.
          // this needs to happen per shape, as each shape can have different strokWidth depending on user configuration when shape was added
          // no need to do this for text nodes they're always left to right.

          if(type != 'Text') {
            const shapeDirection = getDrawDirection(x, y, x1, y1);

            switch(shapeDirection){
              case 'right-to-left-bottom-to-top':
                x += strokeWidth;
                x1 -= strokeWidth;
                y += strokeWidth;
                y1 -= strokeWidth;
                break;
              case 'left-to-right-bottom-to-top':
                x -= strokeWidth;
                x1 += strokeWidth;
                y += strokeWidth;
                y1 -= strokeWidth;
                break;
              case 'right-to-left-top-to-bottom':
                x += strokeWidth;
                x1 -= strokeWidth;
                y -= strokeWidth;
                y1 += strokeWidth;
                break;
              case 'left-to-right-top-to-bottom':
                x -= strokeWidth;
                x1 += strokeWidth;
                y -= strokeWidth;
                y1 += strokeWidth;
                break;
            }
          }

          minX = Math.min(x, x1, minX);
          maxX = Math.max(x, x1, maxX);
          minY = Math.min(y, y1, minY);
          maxY = Math.max(y, y1, maxY);
        });

        const maxHeight = vm.$refs.annotateCanvas.clientHeight;
        const maxWidth = vm.$refs.annotateCanvas.clientWidth;
        const stageSize = { width: maxX - minX, height: maxY - minY }
        const overSizedStage = maxHeight < stageSize.height || maxWidth < stageSize.width;

        let newScale = { x: 1, y: 1 };

        if(overSizedStage){
          const scaleX = maxWidth / stageSize.width;
          const scaleY = maxHeight / stageSize.height;
          const realScale = Math.min(scaleX, scaleY);
          newScale["x"] = realScale;
          newScale["y"] = realScale;
          stageSize["width"] = stageSize["width"] * realScale;
          stageSize["height"] = stageSize["height"] * realScale;
          // scale factor is used when setting up pixel density prior saving, if the image is scaled
          // update the scale factor to fix pixel density.
          vm.scaleFactor = realScale;
        }
        vm.stage.size(stageSize).scale(newScale).offset({ x: minX, y: minY });
        vm.scrollBarX.visible(false);
        vm.scrollBarY.visible(false);
        vm.baseLayer.position({x: 0, y:0 }).scale({x: 1, y: 1});
        vm.layer.position({x: 0, y:0 }).scale({x:1, y: 1});
        vm.stage.draw();
      },
      bindShapeEveventListeners: function(node) {
        const vm = this;

        // if current shape is a Line transformer, it means it's one of our custom transformable shapes
        // (arrow, line or highligher) if that's the case we need to attach the listeners
        // to the first child of the group which is the actual line/arrow/highliger shape
        // rather than the group, attaching event listeners to the whole group causes unwanted behavior.
        const shape = node.constructor.name == 'LineTransformer' ? node.getNode() : node;

        shape.on('mouseover', () => {
          vm.overShape = true;
          if(!vm.drawing){
            document.body.style.cursor = 'move';
          }
        });

        shape.on('mouseout', () => {
          vm.overShape = false;
          document.body.style.cursor = 'default';
        });

        if(shape.constructor.name == "Text") {
          shape.on('transform', function() {
            // text transform just resizes does not transforms.
            this.setAttrs({
              width: this.width() * this.scaleX(),
              scaleX: 1,
            });
          });

          shape.on('dblclick', function(){ vm.editText(shape) });
        }

        if(shape.constructor.name == "Image" && shape.attrs.pixelate) {
          shape.on('transformstart dragstart', function(){
            shape.opacity(0);
            vm.layer.draw();
          })

          shape.on('transformend dragend', vm.cropAndPixelate);
        }

        if(shape.constructor.name == "Rect" && shape.attrs.crop) {
          shape.on('transform dragmove', vm.centerConfirmationCropBox)
        }

        shape.on('mousedown', function(e){
          e.cancelBubble = true;
          // Canceling event bubbling makes the group that holds the arrow, not to react to mousedown
          // needed for draggable: true to work.
          // Cancelation is necessary because we don't want the stage to react to mousedown on the handler
          // defined in line 204
          // So we need to manually propagate the event to the group that holds lines anchors
          if(this.parent.constructor.name == "LineTransformer"){
            this.parent.fire(e.type, e)
          }
          vm.removeEmptyTextNode();
          // stops any ongoing transformation on another shape and inits transformation on this shape.
          vm.stopTransformation();
          vm.initTransformation(this);
        })

        shape.on('transformstart', function(){
          vm.currentTransformation.transforming = true;
        })

        shape.on('transformend', function(){
          vm.currentTransformation.transforming = false;
          // keep track of text nodes width and height for adjusting stage boundaries before save
          if (this.constructor.name == "Text") {
            this.attrs.realWidth = vm.currentTransformation.width();
            this.attrs.realHeight = vm.currentTransformation.height();
          }
        })
      },
      createArrow: function(){
        let vm = this;
        const pos = vm.stage.getScaledPointerPosition();
        const group = new Konva.LineTransformer({
          draggable: true
        })

        const arrow = new Konva.Arrow({
          points: [pos.x, pos.y , pos.x, pos.y],
          pointerLength: 20,
          pointerWidth: 20,
          fill: vm.selectedColor,
          stroke: vm.selectedColor,
          strokeWidth: vm.selectedThickness,
          strokeScaleEnabled: false,
        });

        group.add(arrow);

        return group;
      },
      createLine: function(){
        let vm = this;
        const pos = vm.stage.getScaledPointerPosition();
        const group = new Konva.LineTransformer({
          draggable: true
        })

        const line = new Konva.Line({
          points: [pos.x, pos.y , pos.x, pos.y],
          lineCap: 'round',
          stroke: vm.selectedColor,
          strokeWidth: vm.selectedThickness,
          strokeScaleEnabled: true,
        });

        group.add(line);

        return group;
      },
      createHighlighter: function(){
        let vm = this;
        const pos = vm.stage.getScaledPointerPosition();
        const group = new Konva.LineTransformer({
          draggable: true
        })

        const highligher = new Konva.Line({
          points: [pos.x, pos.y , pos.x, pos.y],
          lineCap: 'round',
          stroke: vm.selectedColor,
          strokeWidth: vm.selectedThickness * 4,
          strokeScaleEnabled: true,
          opacity: 0.5,
        });

        group.add(highligher)

        return group;
      },
      createEllipse: function(){
        let vm = this;
        const pos = vm.stage.getScaledPointerPosition();
        return new Konva.Ellipse({
          x: pos.x,
          y: pos.y,
          radiusX: 0,
          radiusY: 0,
          stroke: vm.selectedColor,
          strokeWidth: vm.selectedThickness,
          strokeScaleEnabled: false,
          draggable: true
        });
      },
      createNumber: function(){
        let vm = this;
        const pos = vm.stage.getScaledPointerPosition();
        const radius = 20;

        return new Konva.CircledNumber({
          x: pos.x,
          y: pos.y,
          draggable: true,
          radius: radius,
          circle: {
            fill: vm.selectedColor,
            stroke: 'white',
            strokeWidth: vm.selectedThickness,
            strokeScaleEnabled: true,
            shadowColor: "#000",
            shadowForStrokeEnabled: true,
            shadowOpacity: 0.7,
            shadowBlur: 2,
            shadowOffset: { x: 0, y: 2 },
            perfectDrawEnabled: false
          },
          text: {
            text: this.numberToolIncrement++,
            verticalAlign: 'middle',
            align: 'center',
            fontSize: 15,
            fontFamily: 'Arial',
            fill: 'white',
            perfectDrawEnabled: false
          }
        });
      },
      createRect: function(){
        let vm = this;
        const pos = vm.stage.getScaledPointerPosition();
        return new Konva.Rect({
          x: pos.x,
          y: pos.y,
          width: 0,
          height: 0,
          stroke: vm.selectedColor,
          strokeWidth: vm.selectedThickness,
          strokeScaleEnabled: false,
          draggable: true
        });
      },
      createDraw: function(){
        let vm = this;
        const pos = vm.stage.getScaledPointerPosition();
        return new Konva.Line({
            stroke: vm.selectedColor,
            strokeWidth: vm.selectedThickness,
            strokeScaleEnabled: true,
            globalCompositeOperation: "brush",
            points: [pos.x, pos.y],
            draggable: true,
            drawing: true
          });
      },
      createText: function(initialPosition = null) {
        let vm = this;
        const pos = initialPosition || vm.stage.getScaledPointerPosition();
        const strokeWidth = vm.outlineWidths[vm.selectedFontSize];
        // there's an empty text shape?, destroy the empty text before creating a new one
        vm.removeEmptyTextNode()

        return new Konva.Text({
          x: pos.x,
          y: pos.y,
          fontSize: vm.selectedFontSize,
          draggable: true,
          fontStyle: 'bold',
          stroke: '#FFF',
          strokeEnabled: vm.outlineText,
          strokeScaleEnabled: true,
          fontFamily: 'SFpro',
          fillAfterStrokeEnabled: true,
          strokeWidth: strokeWidth,
          fill: vm.selectedColor,
          shadowColor: "#000",
          shadowForStrokeEnabled: true,
          shadowOpacity: 0.7,
          shadowBlur: 4,
          shadowOffset: { x: 0, y: 2 },
          perfectDrawEnabled: false,
          width: 250
        });
      },
      createImage: function(img, key, width = null, height = null, position = null) {
        const vm = this;
        const pos = position || vm.stage.getScaledPointerPosition();
        // need to save the img to be used when copy/pasting, redoing and assign to the new konva node;
        if(!vm.images[key]){ vm.images[key] = img; }
        return new Konva.Image({
          x: pos.x,
          y: pos.y,
          key: key,
          image: img,
          width: width || img.width,
          height: height || img.height,
          draggable: true
        });
      },
      createPixelate: function(){
        let vm = this;
        const pos = vm.stage.getScaledPointerPosition();
        return new Konva.Image({
          x: pos.x,
          y: pos.y,
          width: 0,
          height: 0,
          dash: [5,5],
          stroke: '#000',
          strokeWidth: 1,
          strokeScaleEnabled: true,
          draggable: true,
          pixelate: true
        });
      },
      createCrop: function(){
        let vm = this;
        const pos = vm.stage.getScaledPointerPosition();
        return new Konva.Rect({
          x: pos.x,
          y: pos.y,
          width: 0,
          height: 0,
          dash: [5,5],
          stroke: '#000',
          strokeWidth: 1,
          strokeScaleEnabled: true,
          draggable: true,
          crop: true
        });
      },
      cropSelection: function(){
        // This function is bound to the current shape dimensions, which is the selected area.
        // returns a base64 image
        const vm = this;
        const baseImageClone = vm.baseImage.clone();

        // get the absolute x,y, width, height values for the image relative to the stage
        const realImageSize = vm.baseImage.getClientRect();

        // get the absolute x,y,width and height values of the selection relative to the stage;
        const selection = vm.currentShape.getClientRect();

        // baseImage has some padding, position is never 0,0, take that into account and add it to the current selection
        const coordinates = { x: selection.x - realImageSize.x, y: selection.y - realImageSize.y }

        const crop = {
          x: coordinates.x,
          y: coordinates.y,
          width: selection.width,
          height: selection.height
        }

        // this is the size of the shape relative to the stage considering scale
        const relativeShapeSize = vm.currentShape.size();
        const absWidth = Math.abs(relativeShapeSize.width);
        const absHeight = Math.abs(relativeShapeSize.height);

        // need to make sure image starting position is always top left corner
        // it was drawn from right to left
        if(vm.currentShape.width() <= 0){ vm.currentShape.x(vm.currentShape.x() - absWidth) }
        // it was drawn from bottom to top
        if(vm.currentShape.height() <= 0){ vm.currentShape.y(vm.currentShape.y() - absHeight) }
        // if selection is made from bottom to top or right to left, the dimensions are negative
        // this makes sure the selection always has a positive width and height
        vm.currentShape.size({ width: absWidth, height: absHeight });

        // need a new layer to put our copy of the baseImage with position 0,0
        const clipLayer = new Konva.Layer()

        // put base image clone in a temporary layer in top left corner with scaled size the user is seeing
        baseImageClone.size({ width: realImageSize.width, height: realImageSize.height })
        baseImageClone.position({ x: 0, y: 0 });

        // set the same dimensions for layer
        clipLayer.size(baseImageClone.size());
        clipLayer.add(baseImageClone);
        // clip the layer on the real selection position
        clipLayer.clip(crop);
        // pass in the clip position so toDataURL so we just get the clip region exported
        return clipLayer.toDataURL(crop);
      },
      centerConfirmationCropBox: function(){
        const vm = this;
        const nodeDimendions = vm.currentShape.getClientRect();
        const boxDimensions = vm.$refs.cropConfirmation;

        // put the confirmation box in the center of the crop selection
        const confirmationBoxTop = nodeDimendions.y + (nodeDimendions.height / 2) - (boxDimensions.clientHeight / 2) + 'px';
        const confirmationBoxLeft = nodeDimendions.x + (nodeDimendions.width / 2) - (boxDimensions.clientWidth / 2)+ 'px';
        vm.$refs.cropConfirmation.style.top = confirmationBoxTop;
        vm.$refs.cropConfirmation.style.left = confirmationBoxLeft;
      },
      cancelCrop: function(){
        this.cropping = false;
        this.currentShape.remove();
        this.currentShape = null;
        this.stopTransformation();
        this.layer.draw();
      },
      confirmCrop: function(){
        const vm = this;
        vm.cropping = false;
        const image = new Image();
        const imgSrc = vm.cropSelection()

        // remove the selection rect from it's parent (draw layer) and after crop selection is made
        vm.currentShape.remove();
        // nullify the reference so current shape is really empty for the component
        vm.currentShape = null;

        image.src = imgSrc;
        image.onload = function(){
          // in case the selection was dragged, we need to remove the transformation shape
          vm.stopTransformation();
          vm.setBaseImageAndScale(this);
          vm.scale(vm.scaleFactor);
          vm.layer.draw();

          // need to save the stage state til the selection node is destroyed so it's not tracked
          vm.saveStageState();
        }
      },
      cropAndPixelate: function(){
        const vm = this;
        const img = new Image();
        img.src = vm.cropSelection();
        img.onload = function(){
          // remove stroke line and set image
          const imgKey = `pixelate-${Date.now()}`;
          vm.currentShape.setAttrs({
            // remove dashed stroke
            strokeWidth: 0,
            opacity: 1,
            key: imgKey,
            image: this,
          })
          if(!vm.images[imgKey]){ vm.images[imgKey] = this; }


          // needs to be cache according to konva documentation for filters to work
          vm.currentShape.cache();
          vm.currentShape.filters([Konva.Filters.Pixelate]);
          vm.currentShape.pixelSize(10 * window.devicePixelRatio);
          // make sure this is always behind the other shapes
          vm.currentShape.zIndex(0);
          // image could be hidden because of transformstart and dragstart
          vm.saveStageState()
          vm.layer.draw();
        }
      },
      copy: function(e){
        const vm = this;
        if(!vm.currentTransformation) { return; }

        e.preventDefault();
        let shape;

        if(vm.currentTransformation.constructor.name == 'LineTransformer') {
          shape = vm.currentTransformation
          shape.removeAnchors();
        } else {
          shape = vm.currentTransformation.getNode()
        }

        e.clipboardData.setData('text/json', shape.toJSON());
      },
      cut: function(e) {
        const vm = this;
        if(!vm.currentTransformation) { return; }

        e.preventDefault();
        document.body.style.cursor = 'default';

        let cutNode;
        // If it's a LineTransformer rather than a Transformer, it means it our custom transformer object.
        if(vm.currentTransformation.constructor.name == 'LineTransformer') {
          cutNode = vm.currentTransformation
          cutNode.removeAnchors();
        } else {
          cutNode = vm.currentTransformation.getNode()
        }

        vm.overShape = false;
        e.clipboardData.setData('text/json', cutNode.toJSON());

        cutNode.destroy();
        vm.stopTransformation();
      },
      editText: function(textNode) {
        const vm = this;

        if(vm.currentTransformation) { vm.currentTransformation.hide(); }
        textNode.hide();
        vm.textEditing = true;
        vm.layer.draw();

        const textPosition = textNode.absolutePosition();
        const stageBox = vm.stage.container().getBoundingClientRect();
        const areaPosition = {
          x: stageBox.left + textPosition.x,
          y: stageBox.top + textPosition.y,
        };

        const textarea = document.createElement('textarea');
        document.body.appendChild(textarea);
        textarea.value = textNode.text();
        textarea.style.position = 'absolute';
        textarea.style.top = areaPosition.y + 'px';
        textarea.style.left = areaPosition.x + 'px';
        textarea.style.width = textNode.width() - textNode.padding() * 2 + 'px';
        textarea.style.height =
        textNode.height() - textNode.padding() * 2 + 5 + 'px';
        textarea.style.fontSize = textNode.fontSize() + 'px';
        textarea.style.border = 'none';
        textarea.style.padding = '0px';
        textarea.style.margin = '0px';
        textarea.style.overflow = 'hidden';
        textarea.style.background = 'none';
        textarea.style.outline = 'none';
        textarea.style.resize = 'none';
        textarea.style.lineHeight = textNode.lineHeight();
        textarea.style.fontFamily = textNode.fontFamily();
        textarea.style.transformOrigin = 'left top';
        textarea.style.textAlign = textNode.align();
        textarea.style.color = textNode.fill();
        let rotation = textNode.rotation();
        let transform = '';
        if (rotation) {
          transform += 'rotateZ(' + rotation + 'deg)';
        }

        let px = 0;
        // also we need to slightly move textarea on firefox
        // because it jumps a bit
        const isFirefox =
          navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
        if (isFirefox) {
          px += 2 + Math.round(textNode.fontSize() / 20);
        }
        transform += 'translateY(-' + px + 'px)';

        textarea.style.transform = transform;

        // reset height
        textarea.style.height = 'auto';
        // after browsers resized it we can set actual value
        textarea.style.height = textarea.scrollHeight + 3 + 'px';

        textarea.focus();

        function saveText() {
          const text = textarea.value.trim();

          if (text !== "") {
            textNode.text(text);
            if (vm.currentTransformation) { vm.currentTransformation.show(); }
            textNode.attrs.realWidth = textarea.clientWidth;
            textNode.attrs.realHeight = textarea.clientHeight;
            vm.saveStageState()
          } else {
            textNode.attrs.hasText = false;
          }
          removeTextarea();
        }

        function blurHandler() {
          const text = textarea.value.trim();
          if(text != "") {
            textNode.text(text);
            textNode.attrs.hasText = true;
            textNode.attrs.realWidth = textarea.clientWidth;
            textNode.attrs.realHeight = textarea.clientHeight;
            vm.saveStageState();
          } else {
            textNode.attrs.hasText = false;
          }
          removeTextarea();
        }

        function textAreaKeyDownHandler(e){
          e.stopPropagation();
          vm.currentShape.attrs.hasText = true;

          if (e.keyCode === 13 && !e.shiftKey) {
            saveText();
          }

          // on Cmd / Ctrl + S, upload current changes
          if ((e.ctrlKey || e.metaKey) && e.keyCode === 83) {
            e.preventDefault();
            saveText();
            EventBus.$emit('annotate:upload');
          }

          // on esc do not set value back to node
          if (e.keyCode === 27) {
            if(vm.currentTransformation) { vm.currentTransformation.show(); }
            vm.currentShape.attrs.hasText = false;
            removeTextarea();
          }
        }

        function resizeHandler(e) {
          const scale = textNode.getAbsoluteScale().x;
          let newWidth = textNode.width() * scale
          if (!newWidth) {
            // set width for placeholder
            newWidth = textNode.placeholder.length * textNode.fontSize();
          }

          textarea.style.width = newWidth + 'px';
          textarea.style.height = 'auto';
          textarea.style.height = textarea.scrollHeight + textNode.fontSize() + 'px';
        }

        function removeTextarea() {
          // removing an element, triggers a blur, a this point we don't need it anymore.
          textarea.removeEventListener('blur', blurHandler);
          textarea.parentNode.removeChild(textarea);
          textNode.show();
          vm.textEditing = false;
          vm.layer.draw();
        }

        textarea.addEventListener('keydown', textAreaKeyDownHandler);
        textarea.addEventListener('blur', blurHandler);
        textarea.addEventListener('keydown', resizeHandler);
      },
      redrawChange: function (konvaState) {
        const vm = this;
        // if they undid while over a shape (with hotkeys), mouse events would no longer restore this
        vm.overShape = false;
        document.body.style.cursor = 'default';

        vm.layer.destroy();
        // if there's nothing else to undo (not applying for redo)
        // add a new empty layer after destroying the last change.
        if (!konvaState){
          vm.layer = new Konva.Layer();
          vm.stage.add(vm.layer);
          vm.layer.draw();
          return;
        }

        vm.baseLayer.destroy();
        // this is the stage as json, it has 3 childrens baseLayer, drawLayer and scrollbarLayer.
        // we need to apply changes to base and draw layer. index 0 and 1 respectively
        const stage = JSON.parse(konvaState);
        const baseLayerState = _.find(stage.children, (layer) => layer.attrs.id == 'baseLayer')
        const drawLayerState = _.find(stage.children, (layer) => layer.attrs.id == 'drawLayer')
        vm.baseLayer = Konva.Node.create(baseLayerState);
        vm.layer = Konva.Node.create(drawLayerState);
        // need to rescale after cloning layer from previous changes
        // so we don't loose relative position of the cursor

        vm.restoreImageObjects();

        vm.baseImage = vm.baseLayer.children[0];
        vm.stage.add(vm.baseLayer);
        vm.stage.add(vm.layer);

        // if undo/redp involves croping, scale could be different, need to reeadjust
        // to the base image dimensions
        const maxHeight = vm.$refs.annotateCanvas.clientHeight;
        const maxWidth = vm.$refs.annotateCanvas.clientWidth;
        const img = vm.baseImage.image();
        const yScale = maxHeight < img.height ? (maxHeight * 100 / img.height) / 100 : 1;
        const xScale = maxWidth < img.width ? (maxWidth * 100 / img.width) / 100 : 1;
        vm.scaleFactor = Math.min(xScale, yScale);

        vm.scale(vm.scaleFactor);

        // Need to add event listeners to all shapes again.
        for (let i = 0; i < vm.layer.children.length; i++){
          const shape = vm.layer.children[i];
          if(shape.constructor.name == 'Text') { shape.visible(true); }
          // Remove restored anchors for line transformer, in this point. They don't do anything,
          // clickign on the shape would redraw them, just as any transformer node does
          if(shape.constructor.name == 'LineTransformer'){ shape.removeAnchors(); }
          vm.bindShapeEveventListeners(shape);
        }


        vm.layer.draw();
        vm.baseLayer.draw();
        vm.stage.draw();
      },
      undoChange: function () {
        const vm = this;
        if(!vm.undoEnabled) { return; }

        const lastChange = vm.changes.undo.pop();
        const lastPosition = Math.max(vm.changes.undo.length - 1, 0);
        const lastState = vm.changes.undo[lastPosition];
        vm.changes.userUndone = true;
        vm.changes.redo.push(lastChange);

        this.redrawChange(lastState);
      },
      redoChange: function () {
        const vm = this;

        if(!vm.redoEnabled) { return; }
        const redo = vm.changes.redo.pop();
        vm.changes.undo.push(redo);

        this.redrawChange(redo);
      },
      dragOver: function(e){
        e.preventDefault();
      },
      drop: function(e){
        const vm = this;

        e.preventDefault();
        e.stopPropagation();
        const file = e.dataTransfer.files[0];
        if(!file.type.match(/image/)){ return; }

        let img = new Image();
        img.onload = function(){
          const pos = vm.stage.getScaledPointerPosition();
          const imgKey = `${file.name}${file.lastModified}`.replace(/\s/g,'');
          // make image 30% its size when dropping
          const width = (this.width / 100) * 30;
          const height = (this.height / 100) * 30;

          vm.createImage(this, imgKey, width, height);
          vm.layer.add(vm.currentShape);
          vm.bindShapeEveventListeners(vm.currentShape);
          vm.finishShape();
          vm.initTransformation(vm.currentShape);
        };

        const reader = new FileReader();
        reader.onload = (function (aImg) { return function (e) { aImg.src = e.target.result; }; })(img);
        reader.readAsDataURL(file);
      },
      finishShape: function(){
        const vm = this;
        if(!vm.currentShape) { return; }

        vm.drawing = false;

        // selected tool could be different than current shape, possible transformation on going,
        // get the shape from the contstructor name rather than selected tool.
        const shape = vm.currentShape.constructor.name;
        this.$emit('update:dirty', true)

        if(shape == 'Text') {
          // Trigger dblclick after text is drawn, to allow editting.
          if(!vm.currentTransformation){
            var event = new MouseEvent('dblclick', {
              'view': window,
              'bubbles': true,
              'cancelable': true
            });
            vm.currentShape.dispatchEvent(event);
          }


          if(vm.currentShape.attrs.hasText) {
            vm.saveStageState()
          }

          // need to return early so we don't push this into changes with saveStageSate
          // as at this point text is blank and it's useless to save current stage state
          return;
        }

        // pixelate tool behaves different, as it's all relative to baseImage not the layer.
        // also need to call saveStageState until crop is finished that's why we return early.
        if(vm.selectedTool == 'pixelate' && vm.currentShape.attrs.pixelate) { return vm.cropAndPixelate(); }

        let shouldRemove = false;
        let x, y, x1, y1;
        // If shape width or height is 0, or ends at the same point where it started (for lines)
        switch(shape) {
          case "Ellipse":
          case "Rect":
            let shapeSize = vm.currentShape.getClientRect({ skipStroke: true });
            shouldRemove = shapeSize.width == 0 || shapeSize.height == 0;
            break;
          // Even though line lives inside a group, for cases like free drawing, it doesn't.
          // Need to handle it here
          case "Line":
            [x, y, x1, y1] = vm.currentShape.points();
            if(vm.currentShape.attrs.drawing) {
              // drawings don't need 2 starting points,
              // if second point is undefined it means the drawing is just 1 pixel wide, remove it.
              shouldRemove = (typeof(x1) == "undefined" && typeof(y1) == "undefined");
            }else{
              shouldRemove = (x == x1 && y == y1)
            }
            break;
          case "LineTransformer":
            if(vm.currentShape.getNode()){
              [x, y, x1, y1] = vm.currentShape.getNode().points();
              shouldRemove = (x == x1 && y == y1)
            }
            break;
        }

        // need to ask for both, the selected tool can be crop, but we could be transforming a different kind of node
        if(vm.selectedTool == 'crop' && vm.currentShape.attrs.crop) {
          if(shouldRemove) {
            vm.currentShape.destroy();
            vm.layer.draw();
          }else{
            vm.showCropConfirmation();
          }

          // dont need to save the stage state at this point
          // we don't want the crop selection sticking around
          // until the crop confirmation is made, then we save state
          // so we return early here.
          return;
        }

        if(shouldRemove) {
          vm.currentShape.destroy();
          vm.layer.draw();
        } else {
          vm.saveStageState();
        }

        // Attach listeners to arrow finshed, to listen for keyboard input and create text nodes right below the arrow.
        if(!shouldRemove && vm.selectedTool == "arrow") {
          vm.typingAfterArrow = true;
          vm.textAfterArrowTimeout = setTimeout(() => {
            vm.typingAfterArrow = false;
          }, 3000);
        }
      },
      handleKeyDown: function (e) {
        if (this.typingAfterArrow) {
          return this.textAfterArrow(e);
        }
      },
      initShape: function(e){
        const vm = this;
        if(e.evt.button != 0) return;
        if(vm.overShape) return;
        if(vm.currentTransformation) return;
        // reset changes after a new shape is added and if user was doing/undoing
        if(vm.changes.userUndone) {
          vm.changes.redo = [];
          vm.changes.userUndone = false;
        }

        // don't allow another crop, if user didn't like first selection and tries to select another region
        // we need to destroy the previous one
        if(vm.currentShape?.attrs?.crop) {
          vm.cropping = false;
          vm.currentShape.destroy()
          vm.currentShape = null;
          vm.layer.draw();
        }

        vm.drawing = true;
        // Add Line, Draw, Ellipse, etc....
        const toolCapitalized = vm.selectedTool.charAt(0).toUpperCase() + vm.selectedTool.slice(1)
        vm.currentShape = vm[`create${toolCapitalized}`]()
        vm.layer.add(vm.currentShape);
        vm.bindShapeEveventListeners(vm.currentShape);
        if(vm.selectedTool == 'number'){
          // Numbers can be drawn as soon as the user clicks, no need for mousemove
          vm.layer.draw();
        }
      },
      addAnchorsToLine: function(shape) {
        const vm = this;
        const group = shape.parent;
        const linePoints = shape.points();
        const direction = getDrawDirection(...linePoints);
        let [x, y, x1, y1] = linePoints;
        let startAnchorPos = {};
        let endAnchorPos = {};

        // calculate the angle and relative angle of the line in the quadrant
        const { angle, relativeAngle } = getLineAngles(...linePoints)

        // Angles closer to the y axis tend to extrapolate longer, control the offset used
        // based on the angle. 25 and 20 are arbitraty values that resulted on nice looking spacing between
        // line and dragging anchors, 90 is the total degrees per quadrant.
        const step = (relativeAngle > 45 ? 25 : 20) / 90;
        let offset = step * (90 - relativeAngle);

        // add offsets to proper starting/ending points based on the direction of the arrow
        // As we use line points for our extrapolations, we need the next X position in both anchors
        // so we can calculate extrapolation and get Y for both.
        // Offset is either added or substracted based on the arrow direction, so we know where in X to add it,
        // after the line or before the line
        switch(direction){
          case 'right-to-left-bottom-to-top':
          case 'right-to-left-top-to-bottom':
            startAnchorPos.x = x + offset;
            endAnchorPos.x = x1 - offset;
            break;
          case 'left-to-right-bottom-to-top':
          case 'left-to-right-top-to-bottom':
            startAnchorPos.x = x - offset;
            endAnchorPos.x = x1 + offset;
            break;
        }

        // extrapolate points for initial anchor and final anchor
        // find out Y for anchors, by extrapolating using already knowing X anchor position
        startAnchorPos.y = linear([startAnchorPos.x], [x,x1], [y,y1])[0];
        endAnchorPos.y = linear([endAnchorPos.x], [x,x1], [y,y1])[0];

        // in case the arrow is a perfect 90/270 angle the offset and extrapolation is not really accurate
        // we can override the extrapolation result as we know these are right angles in the corresponding quadrant.
        switch(angle){
          case 90:
            startAnchorPos = { x: x, y: y + 10 };
            endAnchorPos = { x: x1, y: y1 - 15 };
            break;
          case 270:
            startAnchorPos = { x: x, y: y - 10 };
            endAnchorPos = { x: x1, y: y1 + 15 };
            break;
        }


        // create anchors with calculated positions
        const startAnchor = new Konva.Circle({
          x: startAnchorPos.x,
          y: startAnchorPos.y,
          radius: 5,
          fill: 'white',
          stroke: 'rgb(0, 161, 255)',
          strokeWidth: 1,
          draggable: true
        })

        const endAnchor = new Konva.Circle({
          x: endAnchorPos.x,
          y: endAnchorPos.y,
          radius: 5,
          fill: 'white',
          stroke: 'rgb(0, 161, 255)',
          strokeWidth: 1,
          draggable: true
        })

        group.add(startAnchor);
        group.add(endAnchor);

        // whenever an anchor is being pressed down, we need to think as if the transformation is happening
        // this is needed because of an event listener at stage level that destroys any transformer that's not being used
        startAnchor.on('mousedown', () => group.transforming = true)
        endAnchor.on('mousedown', () => group.transforming = true)
        startAnchor.on('dragend', () => group.transforming = false)
        endAnchor.on('dragend', () => group.transforming = false)

        // Change the cursor style whenever mouse is over the anchors or leaves them.
        startAnchor.on('mouseleave', () => document.body.style.cursor = 'default')
        startAnchor.on('mouseover', () => document.body.style.cursor = 'pointer')
        endAnchor.on('mouseleave', () => document.body.style.cursor = 'default')
        endAnchor.on('mouseover', () => document.body.style.cursor = 'pointer')


        // add handler so whenever the anchor is dragged, we resize the line.
        startAnchor.on('dragmove', () => resizeLineFrom('start'));
        endAnchor.on('dragmove', () => resizeLineFrom('end'));

        function resizeLineFrom(movingAnchor){
          // resize accordingly to extrapolation
          // udpate anchors position based on new position, angle and interpolation
          let [x, y, x1, y1] = shape.points();
          const startAnchorPos = startAnchor.position();
          const endAnchorPos = endAnchor.position();
          const { relativeAngle } = getLineAngles(x, y, x1, y1)
          const direction = getDrawDirection(...shape.points());

          // Angles closer to the y axis tend to extrapolate longer, control the offset used
          // based on the angle. 30 and 25 are arbitraty values that resulted on nice looking spacing between
          // line and dragging anchors, 90 is the total degrees per quadrant.
          const step = (relativeAngle > 45 ? 25 : 20) / 90;

          // offset for our interpolations/extrapolations
          let offset = step * (90 - relativeAngle);

          let newLinePoints = {};
          // new anchor x and new anchor y.
          // need to adjust the position of the anchor that is not being dragged
          let newStartAnchorX, newStartAnchorY, newEndAnchorX, newEndAnchorY;

          // add offsets to proper starting/ending points based on the direction of the arrow
          // needed to properly calculate the interpolation, using the points at the edges, in this case
          // the anchors
          switch(direction){
            case 'right-to-left-bottom-to-top':
            case 'right-to-left-top-to-bottom':
              newLinePoints.x = startAnchorPos.x - offset;
              newLinePoints.x1 = endAnchorPos.x + offset;
              newStartAnchorX = x + offset;
              newEndAnchorX = x1 - offset;
              break;
            case 'left-to-right-bottom-to-top':
            case 'left-to-right-top-to-bottom':
              newLinePoints.x = startAnchorPos.x + offset;
              newLinePoints.x1 = endAnchorPos.x - offset;
              newStartAnchorX = x - offset;
              newEndAnchorX = x1 + offset;
              break;
          }

          if(movingAnchor == "start"){
            // interpolate starting point based on the anchor positions, leave ending point the as it is
            newLinePoints.x1 = x1
            newLinePoints.y1 = y1
            newLinePoints.y = linear([newLinePoints.x], [startAnchorPos.x, endAnchorPos.x], [startAnchorPos.y, endAnchorPos.y])[0];
            newEndAnchorY = linear([newEndAnchorX], [x, x1], [y, y1])[0];
            endAnchor.position({ x: newEndAnchorX, y: newEndAnchorY })
          }else{
            newLinePoints.x = x
            newLinePoints.y = y
            newLinePoints.y1 = linear([newLinePoints.x1], [startAnchorPos.x, endAnchorPos.x], [startAnchorPos.y, endAnchorPos.y])[0];
            newStartAnchorY = linear([newStartAnchorX], [x, x1], [y, y1])[0];
            startAnchor.position({ x: newStartAnchorX, y: newStartAnchorY })
          }

          shape.points([newLinePoints.x, newLinePoints.y, newLinePoints.x1, newLinePoints.y1]);
          vm.layer.batchDraw();
        }

        return group;
      },
      initTransformation: function(node) {
        const vm = this;
        if(node.parent.constructor.name == "LineTransformer") {
          // Arrow, Line and Highlighter don't make use of Konva built in transformer object,
          // instead its a custom transformer we created, group we create where we can put anchors for resizing and
          // so in this case the Group is the group itself. Check createArrow function
          vm.currentTransformation = vm.addAnchorsToLine(node);
          vm.layer.draw();
          return;
        }

        if(node.constructor.name == "Text") {
          vm.currentTransformation = new Konva.Transformer({
            nodes: [node],
            enabledAnchors: ['middle-left', 'middle-right'],
            anchorCornerRadius: 10,
              // set minimum width of text
            boundBoxFunc: function (oldBox, newBox) {
              newBox.width = Math.max(30, newBox.width);
              return newBox;
            },
          });
        } else {
          const rotateDisabled = node.attrs.pixelate || node.attrs.crop;
          vm.currentTransformation = new Konva.Transformer({
            nodes: [node],
            rotationSnaps: [0, 90, 180, 270],
            anchorCornerRadius: 10,
            rotateEnabled: !rotateDisabled,
            ignoreStroke: true
          });
        }
        vm.layer.add(vm.currentTransformation);
        vm.layer.draw();
      },
      deleteCurrentShape: function () {
        const vm = this;

        if (vm.currentTransformation) {
          let node;
          if(vm.currentTransformation.constructor.name == 'LineTransformer'){
            node = vm.currentTransformation;
          }else {
            node = vm.currentTransformation.getNode();
          }

          vm.overShape = false;
          document.body.style.cursor = 'default';
          node.destroy();
          vm.stopTransformation();
          vm.saveStageState();

          if(vm.layer.children.length == 0){ vm.$emit('update:dirty', false); }
        }
      },
      paste: function(e){
        const vm = this;
        if(vm.textEditing) { return; }

        const item = e.clipboardData.items[e.clipboardData.items.length - 1];
        if(!item){ return; }

        e.preventDefault();
        switch(item.type){
          // text/json would always be our shapes as we set that type on copy, everything else comes as text/plain;
          case "text/json":
            item.getAsString((json) => {
              const vm = this;
              // prevent default only if clipboard is not empty, to allow pasting images from SO clipboard.
              let node = Konva.Node.create(json);
              let positionNode = node;

              // If the currentTransformation is a group, the position is set there, not in its children unlike Transformer Object
              // use it as a reference to the last pasted object.
              if(vm.currentTransformation && vm.currentTransformation.constructor.name == "LineTransformer"){
                positionNode = vm.currentTransformation
              }else{
                positionNode = vm.currentTransformation?.getNode() || node;
              }

              const position = { x: positionNode.x() + 30 , y: positionNode.y() + 30 };
              node.position(position);

              if(node.constructor.name == 'Text') {
                node.visible(true);
                node.attrs.hasText = true;
                node.attrs.realWidth = vm.currentTransformation.width();
                node.attrs.realHeight = vm.currentTransformation.height();
              }

              if(node.constructor.name == 'Image') {
                node.image(vm.images[node.attrs.key])
                if(node.attrs.pixelate){
                  node.cache();
                  node.filters([Konva.Filters.Pixelate]);
                  node.pixelSize(5);
                }
              }

              vm.currentShape = node;
              vm.bindShapeEveventListeners(node);
              vm.layer.add(node);
              vm.stopTransformation();

              if(node.constructor.name == "LineTransformer") {
                // Need to pass only the child, in this case the arrow/line so
                // transformation is initialized in the arrow, not the group.
                vm.initTransformation(node.getNode());
              } else {
                vm.initTransformation(node);
              }
              vm.saveStageState();
              vm.layer.draw();
            });
            break;
          case "image/png":
            vm.stopTransformation();

            const file = item.getAsFile();
            let img = new Image();
            img.onload = function(){
              const pos = { x: vm.stage.width() / 2, y: vm.stage.height() / 2 };
              const imgKey = `${file.name}${file.lastModified}`.replace(/\s/g,'');
              // make image 30% its size
              const width = (this.width / 100) * 30;
              const height = (this.height / 100) * 30;

              vm.createImage(this, imgKey, width, height, pos);
              vm.layer.add(vm.currentShape);
              vm.bindShapeEveventListeners(vm.currentShape);
              vm.finishShape();
              vm.initTransformation(vm.currentShape);
            };

            const reader = new FileReader();
            reader.onload = (function (aImg) { return function (e) { aImg.src = e.target.result; }; })(img);
            reader.readAsDataURL(file);
            break;
        }
      },
      resetCanvas: function(){
        const vm = this;
        if(vm.layer) {
          vm.layer.destroyChildren();
          vm.layer.draw();
        }
      },
      restoreImageObjects: function(){
        // when undoing/redoing, we take whatever layer.toJSON() is, for image shapes we don't really get the image in it
        // just other basic attributes as x,y, etc... we use a 'key' attribute on such nodes to store the image key and here
        // we go trough each of them, and find the image object with the key attribute and assign it to the shape.
        const vm = this;


        // this restores the base Image
        vm.baseLayer.children.forEach((shape, index) => {
          if (shape.constructor.name != "Image") { return; }

          shape.image(vm.images[shape.attrs.key]);
        });

        // this restores all image shapes added on the drawing layer like pixelate images, or copy pasted images
        vm.layer.children.forEach((shape, index) => {
          if (shape.constructor.name != "Image") { return; }

          shape.image(vm.images[shape.attrs.key]);
          if(shape.attrs.pixelate){
            shape.cache();
            shape.filters([Konva.Filters.Pixelate]);
          }
        });
      },
      removeEmptyTextNode: function(){
        let vm = this;
        if(vm.currentShape && vm.currentShape.constructor.name == "Text" && !vm.currentShape.attrs.hasText){
          vm.currentShape.destroy();
        }
      },
      showCropConfirmation: function(){
        this.cropping = true;
        this.centerConfirmationCropBox()
      },
      setBaseImageAndScale: function(img){
        const vm = this;
        const maxHeight = vm.$refs.annotateCanvas.clientHeight;
        const maxWidth = vm.$refs.annotateCanvas.clientWidth;
        const yScale = maxHeight < img.height ? (maxHeight * 100 / img.height) / 100 : 1;
        const xScale = maxWidth < img.width ? (maxWidth * 100 / img.width) / 100 : 1;
        const canvasPadding = 20;
        const imgWidth = img.width - canvasPadding;
        const imgHeight = img.height - canvasPadding;

        // need to save a reference to the image as it is so we can undo/redo and restore
        const baseImagKey = `baseImage-${Date.now()}`;
        if(!vm.images[baseImagKey]){ vm.images[baseImagKey] = img; }

        vm.baseImage = new Konva.Image({
          x: (maxWidth - imgWidth) / 2,
          y: (maxHeight - imgHeight) / 2,
          image: img,
          width: imgWidth,
          height: imgHeight,
          key: baseImagKey
        });

        vm.scaleFactor = Math.min(xScale, yScale)

        if(vm.scaleFactor < 1) {
          // if image doest not fit inside canvas, scale it down and center it
          vm.scalePct = Math.round(vm.scaleFactor * 100)

          const center = {
            x: maxWidth / 2,
            y: maxHeight / 2,
          };

          const newPos = {
            x: center.x - center.x * vm.scaleFactor,
            y: center.y - center.y * vm.scaleFactor,
          };

          vm.baseLayer.scale({x: vm.scaleFactor, y: vm.scaleFactor }).position(newPos);
          vm.layer.scale({x: vm.scaleFactor, y: vm.scaleFactor }).position(newPos);
        }

        // make sure baseLayer only has one children always
        vm.baseLayer.destroyChildren();
        vm.baseLayer.add(vm.baseImage).batchDraw();
      },
      scroll: function(axis, wheelScroll = 0) {
        const vm = this;
        let scrollbar, measure, scaledMeasure;

        if(axis == 'y'){
          scrollbar = vm.scrollBarY;
          measure = 'height';
          scaledMeasure = 'scaledHeight';
        }else {
          scrollbar = vm.scrollBarX;
          measure = 'width';
          scaledMeasure = 'scaledWidth';
        }

        const availableScroll = vm.stage[measure]() - (scrollbar[measure]() + vm.scrollBarPadding * 2);

        // for wheel scrolling only, new position is handled by dragmove events when dragging the srollbars
        if(wheelScroll != 0){
          const axisPosition = scrollbar[axis]();
          let newAxisPosition = axisPosition + wheelScroll;

          // need to cap positions if they go bigger/lower than stage
          if(newAxisPosition < 0) { newAxisPosition = vm.scrollBarPadding; }
          if(newAxisPosition > availableScroll) { newAxisPosition = availableScroll; }

          // setup new scrollbar position manually, only when wheel scrolling
          scrollbar[axis](newAxisPosition);
          vm.scrollLayer.draw();
        }

        const scrollPosition = scrollbar[axis]() - vm.scrollBarPadding;
        const delta = scrollPosition / availableScroll;
        const scrollByPct = delta - scrollbar.attrs.currentDelta;
        const scrollBy = (vm.baseLayer.attrs[scaledMeasure] * scrollByPct) * -1;
        const newPosition = vm.baseLayer[axis]() + scrollBy;
        scrollbar.attrs.currentDelta = delta;

        vm.layer[axis](newPosition).draw();
        vm.baseLayer[axis](newPosition).draw();
      },
      scaleOrScroll: function(e){
        const vm = this;
        const zooming = event.ctrlKey;

        if (zooming) {
          const scaleBy = 1.1
          let newScale = e.evt.deltaY > 0 ? vm.scaleFactor / scaleBy : vm.scaleFactor * scaleBy;
          vm.scale(newScale)
        } else {
          // scroll down
          if(vm.scrollBarY.visible()){
            if(e.evt.deltaY > 0) {
              vm.scroll('y', 20)
            } else if(e.evt.deltaY < 0) {
              vm.scroll('y', -20)
            }
          }

          if(vm.scrollBarX.visible()){
            if(e.evt.deltaX < 0) {
              vm.scroll('x', -20)
            }else if(e.evt.deltaX > 0){
              vm.scroll('x', 20)
            }
          }

        }
      },
      scale: function(newScale){
        const vm = this;
        const zoomIn = vm.scaleFactor < newScale;

        //cap scale between 20% and 200%
        if(newScale > 2){ newScale = 2 }
        if(newScale < .2){ newScale = .2 }

        vm.scaleFactor = newScale;
        vm.scalePct = Math.round(newScale * 100)
        if(vm.scalePct < 20 && !zoomIn){ return; }
        if(vm.scalePct > 200 && zoomIn) { return; }

        const layerSize = vm.baseLayer.size();
        const scaledLayerSize = { width: layerSize.width * newScale, height: layerSize.height * newScale };

        // always center layers when zooming in/out
        const center = {
          x: vm.baseLayer.width() / 2,
          y: vm.baseLayer.height() / 2,
        };

        const newPos = {
          x: center.x - center.x * newScale,
          y: center.y - center.y * newScale,
        };

        const imgWidth = vm.baseImage.width() * newScale;
        const imgHeight = vm.baseImage.height() * newScale;

        // save the real scaled dimensions of the layer
        vm.baseLayer.attrs.scaledHeight = Math.max(scaledLayerSize.height, imgHeight);
        vm.baseLayer.attrs.scaledWidth = Math.max(scaledLayerSize.width, imgWidth);

        const showScrollBars = layerSize.height < imgHeight || layerSize.width < imgWidth;
        vm.scrollBarY.visible(showScrollBars);
        vm.scrollBarX.visible(showScrollBars);

        // always bring scrollbars to center as we always zoom at center position
        vm.scrollBarX.x((layerSize.width - vm.scrollBarX.width()) / 2);
        vm.scrollBarY.y((layerSize.height - vm.scrollBarY.height()) / 2);

        // save current scroll position, always centered
        vm.scrollBarY.attrs.currentDelta = 0.5;
        vm.scrollBarX.attrs.currentDelta = 0.5;

        vm.layer.scale({ x: newScale, y: newScale }).position(newPos).draw();
        vm.baseLayer.scale({ x: newScale, y: newScale }).position(newPos).draw();
        vm.scrollLayer.draw();
      },
      stopTransformation: function(e){
        const transformation = this.currentTransformation;
        if(transformation && !transformation.transforming){
          if(transformation.constructor.name == "LineTransformer"){
            transformation.removeAnchors()
          }else{
            transformation.destroy();
          }
          this.currentTransformation = null;
          this.layer.batchDraw();
        }
      },
      textAfterArrow: function(e){
        e.stopPropagation();
        const vm = this;

        // if any non single char key is pressed, return (ctrl, shift, esc....)
        if(e.key.length > 1){ return; }

        this.typingAfterArrow = false;
        if (vm.textAfterArrowTimeout) {
          clearTimeout(vm.textAfterArrowTimeout);
        }

        vm.textAfterArrowTimeout = null;
        // USe get node because arrows are really groups, we need to extract the actuall arrow/line node
        let [x, y, x1, y1] = vm.currentShape.getNode().getPoints();

        x -= 30;

        // position of text node y axis changes depending of arrow direction
        if(y < y1){
          y -= 40
        }

        vm.currentShape = vm.createText({x, y});
        vm.layer.add(vm.currentShape);
        vm.bindShapeEveventListeners(vm.currentShape);
        vm.finishShape();
      },
      saveStageState: function(){
        const vm = this;
        let undo = vm.changes.undo;
        const previousStageState = undo[undo.length - 1];
        const currentStageState = vm.stage.toJSON();
        // prevent same state to be pushed twice in the array
        if(!previousStageState || currentStageState != previousStageState){
          vm.changes.undo.push(currentStageState)
        }
      },
      updateShape: function(){
        let vm = this;
        if(vm.overShape && !vm.drawing) { return; }
        if(vm.currentTransformation) { return; }
        if(!vm.drawing){ return; }
        if(!vm.currentShape) { return; }

        let newPoints, x, y;
        const pos = vm.stage.getScaledPointerPosition();
        switch(vm.selectedTool) {
          case "arrow":
          case "line":
          case "highlighter":
            const shape = vm.currentShape.getNode();
            newPoints = shape.points().slice(0,2).concat([pos.x, pos.y]);
            shape.points(newPoints);
            break;
          case "draw":
            newPoints = vm.currentShape.points().concat([pos.x, pos.y]);
            vm.currentShape.points(newPoints);
            break;
          case "ellipse":
            [x, y] = [vm.currentShape.x(), vm.currentShape.y()]
            const radiusX = Math.abs(pos.x - x);
            const radiusY = Math.abs(pos.y - y);
            vm.currentShape.radius({ x: radiusX, y: radiusY });
            break;
          case "rect":
          case "pixelate":
          case "crop":
            [x, y] = [vm.currentShape.x(), vm.currentShape.y()]
            var width = pos.x - x;
            var height = pos.y - y;
            vm.currentShape.size({ width, height });
            break;
        }

        vm.layer.batchDraw();
      },
      upload: async function() {
        const vm = this;

        // Edge case, where user does not confirm or cancel cropping, but clicks save anyway. Need to remove the selection.
        if(vm.cropping && vm.currentShape?.attrs?.crop) {
          vm.cropping = false;
          vm.currentShape.destroy()
          vm.currentShape = null;
          vm.layer.draw();
        }

        // Only save the image if there's changes, vm.undoEnabled checks if there are actually changes applied.
        if(!vm.undoEnabled) {
          vm.$nextTick(()=> {
            vm.$emit('update:dirty', false)
          });
          return;
        }

        vm.stopTransformation();
        // batchDraw waits for next tick to draw, make sure we give it enough time before saving
        await new Promise((resolve) => setTimeout(resolve, 50));
        vm.adjustStageBoundaries();
        const pixelRatio = 1 / vm.scaleFactor;
        const dataUrl = vm.stage.toDataURL({ pixelRatio: pixelRatio });
        vm.$emit('update:uploading', true);
        vm.item.links.content_url = dataUrl;
        vm.$store.commit('setItem', vm.item);
        vm.$router.push({ name: 'viewer' });
        vm.$store.dispatch('annotate', { itemId: vm.item.id, dataUrl }).then(()=> {
          if(vm.$route.name != 'annotate'){
            vm.$emit('update:uploading', false);
            vm.$emit('update:dirty', false);
          }
        });
      },
      zoomIn: function(){
        this.scale(this.scaleFactor + .20);
      },
      zoomOut: function(){
        this.scale(this.scaleFactor - .20);
      },
    },
    watch: {
      outlineText: function(newValue, _){
        if(this.currentTransformation){
          const node = this.currentTransformation.getNode();
          if(node.constructor.name == "Text") {
            node.strokeEnabled(newValue);
            this.saveStageState();
            this.layer.draw();
          }
        }
      },
      selectedColor: function(newValue, _) {
        if(this.currentTransformation){
          const node = this.currentTransformation.getNode();
          switch(node.constructor.name){
            case "Text":
              node.fill(newValue);
              break;
            case "Arrow":
              node.fill(newValue);
              node.stroke(newValue);
              break;
            case "CircledNumber":
              node.circle.fill(newValue);
              if(newValue == "#FFFFFF") {
                node.number.fill("#000");
              }else{
                node.number.fill("#FFF");
              }
              break;
            default:
              node.stroke(newValue);
          }
          this.saveStageState();
          this.layer.draw();
        }
      },
      selectedThickness: function(newValue, _) {
        if(this.currentTransformation) {
          const node = this.currentTransformation.getNode();
          if(node.constructor.name == "Text") { return; }

          if(node.constructor.name == "CircledNumber"){
            node.circle.strokeWidth(newValue)
          }else{
            // highligher has opacity of 0.5 and needs to be ticker
            let multiplier = node.opacity() != 1 ? 4 : 1
            node.strokeWidth(newValue * multiplier)
          }

          this.saveStageState();
          this.layer.draw();
        }
      },
      selectedFontSize: function(newValue, _) {
        if(this.currentTransformation) {
          const node = this.currentTransformation.getNode();
          if(node.constructor.name != "Text") { return; }
          const strokeWidth = this.outlineWidths[newValue];
          node.strokeWidth(strokeWidth);
          node.fontSize(newValue);
          this.saveStageState();
          this.layer.draw();
        }
      }
    },
    computed: {
      ...mapState(['item']),
      imageUrl: function() {
        return this.item.links.content_url;
      },
      undoEnabled: function () {
        // Need to account for the initial state that is saved.
        return this.changes.undo.length > 1;
      },
      redoEnabled: function () {
        return this.changes.redo.length > 0;
      },
      undoImage: function(){
        const name = this.undoEnabled ? 'tools-history-back.svg' : 'tools-history-back-disabled.svg';
        return require(`images/annotate/${name}`);
      },
      redoImage: function(){
        const name = this.redoEnabled ? 'tools-history-forward.svg' : 'tools-history-forward-disabled.svg';
        return require(`images/annotate/${name}`);
      }
    }
}
</script>

<style lang="scss" scoped>

  #spinner {
    width: 100px;
    top: calc(50% - 100px);
    position: relative;
  }

  #annotate-canvas, #async-block {
    padding: 0;
    overflow: hidden;
    flex: 1;
    display: flex;
    align-items: center;
    flex-direction: column;
    position: relative;
    outline: none;
  }

  #controllers {
    position: absolute;
    display: flex;
    align-items: center;
    z-index: 1;
    top: 10px;
  }

  #zoom {
    display: flex;
    padding: 0.25rem;
    border-radius: 20px;
    backdrop-filter: blur(16px);
    border: solid 1px rgba(0, 0, 0, 0.15);
    background-color: rgba(255, 255, 255, 0.5);
    font-weight: bold;
    align-items: center;

    span {
      font-size: 12px;
      margin: 0 5px;
    }

    div {
      cursor: pointer;
    }
  }

  #redo, #undo {
    cursor: pointer;
  }

  #history {
    display: flex;
    padding: 0.25rem;
    border-radius: 20px;
    backdrop-filter: blur(16px);
    border: solid 1px rgba(0, 0, 0, 0.15);
    background-color: rgba(255, 255, 255, 0.5);
    font-weight: bold;
    align-items: center;
    margin-left: 0.5rem;

    #undo {
      margin-right: 0.25rem;
    }
  }

  .image {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  #crop-confirmation {
    position: absolute;
    z-index: 1;

    a {
      color: white;
      font-size: .7rem;
      cursor: pointer;
    }

  }
</style>
